import { useState, useEffect } from 'react';
import { useOllamaModels } from '../../hooks/useOllamaModels';

export default function OllamaConfig({ baseUrl, onBaseUrlChange, selectedModel, onModelSelect }) {
  // `baseUrl` is `settings.ollamaBaseUrl`, which now defaults to `null` (SSRF
  // hardening). Seeding a controlled <input> from `null` is React's
  // "uncontrolled → controlled" anti-pattern, so coerce to an empty string.
  const [urlInput, setUrlInput] = useState(baseUrl ?? '');
  const {
    installedModels,
    availableModels,
    serverStatus,
    loading,
    error,
    pulling,
    pullProgress,
    checkStatus,
    pullModel,
    deleteModel,
    isInstalled
  } = useOllamaModels(baseUrl);

  // Sync urlInput with baseUrl prop
  useEffect(() => {
    setUrlInput(baseUrl ?? '');
  }, [baseUrl]);

  const handleUrlSubmit = (e) => {
    e.preventDefault();
    if (urlInput !== baseUrl) {
      onBaseUrlChange(urlInput);
    }
  };

  const handleDelete = async (modelName) => {
    if (confirm(`Delete model "${modelName}"? This cannot be undone.`)) {
      try {
        await deleteModel(modelName);
      } catch (err) {
        // Error is handled in hook
      }
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    const gb = bytes / (1024 * 1024 * 1024);
    if (gb >= 1) return `${gb.toFixed(1)} GB`;
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(0)} MB`;
  };

  const formatProgress = (progress) => {
    if (!progress) return 'Starting...';
    if (progress.status === 'pulling manifest') return 'Pulling manifest...';
    if (progress.status === 'downloading') {
      const percent = progress.completed && progress.total
        ? Math.round((progress.completed / progress.total) * 100)
        : 0;
      return `Downloading... ${percent}%`;
    }
    if (progress.status === 'verifying sha256 digest') return 'Verifying...';
    if (progress.status === 'writing manifest') return 'Finalizing...';
    if (progress.status === 'success') return 'Complete!';
    if (progress.status === 'error') return `Error: ${progress.error}`;
    return progress.status || 'Processing...';
  };

  const getProgressPercent = (progress) => {
    if (!progress || !progress.completed || !progress.total) return 0;
    return Math.round((progress.completed / progress.total) * 100);
  };

  return (
    <div className="ollama-config">
      <div className="ollama-url-section">
        <form className="ollama-url-input" onSubmit={handleUrlSubmit}>
          <input
            type="text"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="http://localhost:11434"
          />
          <button type="submit" disabled={urlInput === baseUrl}>
            Update
          </button>
        </form>
        <div className={`ollama-status ${serverStatus.checking ? 'checking' : serverStatus.available ? 'connected' : 'disconnected'}`}>
          {serverStatus.checking ? (
            'Checking connection...'
          ) : serverStatus.available ? (
            <>● Connected to Ollama server</>
          ) : (
            <>○ Ollama server not available</>
          )}
          {!serverStatus.checking && (
            <button
              style={{ marginLeft: '0.5rem', padding: '0.125rem 0.5rem', fontSize: '0.75rem' }}
              onClick={checkStatus}
            >
              Refresh
            </button>
          )}
        </div>
      </div>

      {serverStatus.available && (
        <div className="ollama-models-section">
          <h3>Installed Models</h3>
          {loading ? (
            <div className="no-models-message">Loading models...</div>
          ) : error ? (
            <div className="no-models-message" style={{ color: 'var(--error)' }}>{error}</div>
          ) : installedModels.length === 0 ? (
            <div className="no-models-message">No models installed. Pull one from below.</div>
          ) : (
            <div className="ollama-models-list">
              {installedModels.map(model => (
                <div
                  key={model.name}
                  className={`ollama-model-item ${model.name === selectedModel ? 'selected' : ''}`}
                >
                  <label className="ollama-model-info">
                    <input
                      type="radio"
                      name="ollama-model"
                      checked={model.name === selectedModel}
                      onChange={() => onModelSelect(model.name)}
                    />
                    <span className="ollama-model-name">{model.name}</span>
                    {model.size && (
                      <span className="ollama-model-size">{formatSize(model.size)}</span>
                    )}
                  </label>
                  <div className="ollama-model-actions">
                    <button onClick={() => handleDelete(model.name)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {pulling && pullProgress && (
            <div className="pull-progress">
              <div className="pull-progress-text">
                Pulling {pulling}: {formatProgress(pullProgress)}
              </div>
              <div className="pull-progress-bar">
                <div
                  className="pull-progress-bar-fill"
                  style={{ width: `${getProgressPercent(pullProgress)}%` }}
                />
              </div>
            </div>
          )}

          <div className="ollama-available-section">
            <h4>Available Models to Download</h4>
            <div className="ollama-available-list">
              {availableModels.map(model => {
                const installed = isInstalled(model.name);
                return (
                  <div
                    key={model.name}
                    className={`ollama-available-item ${installed ? 'installed' : ''}`}
                  >
                    <div>
                      <span className="ollama-available-name">{model.name}</span>
                      {model.size && (
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginLeft: '0.5rem' }}>
                          ({model.size})
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => pullModel(model.name)}
                      disabled={installed || pulling}
                    >
                      {installed ? 'Installed' : pulling === model.name ? 'Pulling...' : 'Pull'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
